import React from 'react';
import { cn } from '../../../lib/utils';

type Tone = 'primary' | 'success' | 'purple' | 'warning' | 'destructive';

export interface TaskyStatCardProps extends React.HTMLAttributes<HTMLDivElement> {
  label: React.ReactNode;
  value: React.ReactNode;
  delta?: React.ReactNode;
  deltaTone?: 'up' | 'down' | 'neutral';
  icon?: React.ReactNode;
  iconTone?: Tone;
}

const iconToneClass: Record<Tone, string> = {
  primary: 'bg-primary/10 text-primary',
  success: 'bg-success/10 text-success',
  purple: 'bg-purple/10 text-purple',
  warning: 'bg-warning/10 text-warning',
  destructive: 'bg-destructive/10 text-destructive',
};

const deltaToneClass = {
  up: 'text-green-600',
  down: 'text-destructive',
  neutral: 'text-gray-500',
};

export const TaskyStatCard = React.forwardRef<HTMLDivElement, TaskyStatCardProps>(
  (
    { className, label, value, delta, deltaTone = 'neutral', icon, iconTone = 'primary', ...rest },
    ref,
  ) => (
    <div
      ref={ref}
      {...rest}
      className={cn(
        'bg-white rounded-tasky-md border border-warm-border p-4',
        className,
      )}
    >
      {icon ? (
        <div className="flex items-center gap-3 mb-2">
          <div
            className={cn(
              'w-10 h-10 rounded-full flex items-center justify-center [&_svg]:size-5',
              iconToneClass[iconTone],
            )}
          >
            {icon}
          </div>
          <span className="text-xs text-gray-500">{label}</span>
        </div>
      ) : (
        <div className="text-sm text-gray-500 mb-1">{label}</div>
      )}
      <div className={cn('text-2xl font-bold text-dark', icon && 'text-xl')}>
        {value}
      </div>
      {delta !== undefined && (
        <div className={cn('text-xs mt-1', deltaToneClass[deltaTone])}>{delta}</div>
      )}
    </div>
  ),
);
TaskyStatCard.displayName = 'TaskyStatCard';
